import { SdgAlignmentStrength } from '@upright/common/model/output/scores/sdg-alignment/types';
import { getAlignmentsOfValenceWithAdjustedValues } from '@upright/common/model/output/scores/sdg-alignment/utils';
import { Bounds, Side } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/types';
import { SdgLensLayout } from '@upright/ui/common/upright/core/impact-lenses/sdg/common/layout';
import { getSdgTextColorOnColoredBackground } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/shared';
import { mapValues } from 'lodash';
import styled from 'styled-components';
import { sideToSdgValence } from '../../common/utils';

type SdgBarTextsProps = {
  side: Side;
  textAreaBounds: ReturnType<SdgLensLayout['getSdgTextsAreaBounds']>;
  shouldShowBreakDown: boolean;
  alignments: ReturnType<typeof getAlignmentsOfValenceWithAdjustedValues>;
};

/**
 * Renders the SDG value texts shown in the lens. If alignment level breakdown is not shown (i.e. if we show the summary percentage),
 * the value is shown outside the bar. If the breakdown is shown, the values are shown inside the bars, if the bar has space to show the value.
 */
export const SdgBarTexts = (props: SdgBarTextsProps) => {
  const { textAreaBounds: textAreas, alignments, shouldShowBreakDown } = props;
  const { weak: weakValue, normal: normalValue, strong: strongValue } = mapValues(alignments, d => d.adjustedValue);
  const {
    sum: sumTextAreaBounds,
    weak: weakTextAreaBounds,
    normal: normalTextAreaBounds,
    strong: strongTextAreaBounds,
  } = textAreas;

  if (!shouldShowBreakDown) {
    const sumValue = weakValue + normalValue + strongValue;
    return <SdgBarTextOutsideBar value={sumValue} side={props.side} area={sumTextAreaBounds} />;
  }

  if (!weakTextAreaBounds || !normalTextAreaBounds || !strongTextAreaBounds) return <></>;

  const valence = sideToSdgValence(props.side);

  const strentghBasedProps: { [strength in SdgAlignmentStrength]: { value: number; areaBounds: Bounds } } = {
    [SdgAlignmentStrength.WEAK]: {
      value: weakValue,
      areaBounds: weakTextAreaBounds,
    },
    [SdgAlignmentStrength.NORMAL]: {
      value: normalValue,
      areaBounds: normalTextAreaBounds,
    },
    [SdgAlignmentStrength.STRONG]: {
      value: strongValue,
      areaBounds: strongTextAreaBounds,
    },
  };

  return (
    <>
      {[SdgAlignmentStrength.WEAK, SdgAlignmentStrength.NORMAL, SdgAlignmentStrength.STRONG].map(strength => (
        <SdgBarTextInsideBar
          key={`bar-${valence}-${strength}`}
          value={strentghBasedProps[strength].value}
          side={props.side}
          areaBounds={strentghBasedProps[strength].areaBounds}
          textColor={getSdgTextColorOnColoredBackground(valence, strength)}
        />
      ))}
    </>
  );
};

const SdgBarTextOutsideBar = (props: { value: number; side: Side; area: Bounds }) => {
  const { side, area } = props;
  const textAnchor = side === Side.RIGHT ? 'start' : 'end';
  return (
    <TextOutsideBar x={side === Side.RIGHT ? area.x1 : area.x2} y={area.cy} textAnchor={textAnchor} dy={`${0.25}em`}>
      {formatValue(props.value)}
    </TextOutsideBar>
  );
};

const TextOutsideBar = styled.text`
  font-size: 20px;
`;

const SdgBarTextInsideBar = (props: { value: number; side: Side; areaBounds: Bounds; textColor?: string }) => {
  const { side, areaBounds } = props;

  const textAnchor = side === Side.RIGHT ? 'start' : 'end';

  if (areaBounds.width < 30) return <></>;

  // Aligns the text roughly to the center of the box. If you change font size, adjust this.
  const xOffsetAbs = -15;
  const xOffset = getSign(side) * xOffsetAbs;

  return (
    <TextInsideBar
      // Tries to go roughly to the center of the bar
      x={areaBounds.cx + xOffset}
      y={areaBounds.cy}
      textAnchor={textAnchor}
      dy={`${0.25}em`}
      textColor={props.textColor}
    >
      {formatValue(props.value)}
    </TextInsideBar>
  );
};

const TextInsideBar = styled.text<{ textColor?: string }>`
  /* When you change the font size, remember to adjust xOffsetAbs */
  font-size: 14px;
  font-weight: bold;
  fill: ${props => props.textColor};
`;

function formatValue(value: number) {
  return `${(value * 100).toFixed(0)}%`;
}

function getSign(side: Side) {
  return side === Side.RIGHT ? 1 : -1;
}
